// FILE: src/blueprint/components/insights/InsightsPanel.jsx
/**
 * GOAL:
 * - Display rail insights and coach entries from canonical entry objects.
 * - Preserve safe rendering for plain/html block bodies.
 * - Keep the rail focused on inline help and coach content only.
 *
 * IMPORTANT:
 * - Regular content/insight entries still use the generic RailSection renderer.
 * - Coach entries are rendered through CoachStream, because coach content
 *   must type sequentially block-by-block instead of rendering as static cards.
 */

import { useMemo } from '@wordpress/element';
import { resolveCoachAvatarSrc } from '../../utils/resolveCoachAvatarSrc.js';
import CoachStream from './CoachStream.jsx';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asString(value) {
  return typeof value === 'string' ? value : '';
}

function splitParagraphs(text) {
  const t = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();

  if (!t) return [];

  return t
    .split(/\n{2,}/g)
    .map((p) => p.trim())
    .filter(Boolean);
}

function splitLines(paragraph) {
  return String(paragraph || '')
    .split('\n')
    .map((l) => l.trimEnd());
}

function isTitleLikeLine(line) {
  const s = String(line || '').trim();
  if (!s) return false;
  if (s.length > 40) return false;
  if (s.includes(':')) return false;
  if (/[.!?]$/.test(s)) return false;
  return true;
}

function formatLabelLine(line) {
  const m = String(line || '').match(/^([A-Za-z][A-Za-z0-9 _-]{0,30}):\s*(.*)$/);
  if (!m) {
    return null;
  }

  const label = m[1];
  const rest = m[2] || '';

  return { label, rest };
}

function stripFutureSentenceAtCompletion(text, progressRatio) {
  if (!(Number(progressRatio) >= 1)) return text;

  return String(text || '')
    .replace(/(^|\s)([^.?!]*\bas you continue\b[^.?!]*[.?!])\s*/gi, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function containsHtml(text) {
  return /<\/?[a-z][\s\S]*>/i.test(String(text || ''));
}

function sanitizeInsightHtml(html) {
  if (typeof document === 'undefined') {
    return String(html || '').trim();
  }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = String(html || '');

  const allowedTags = new Set(['P', 'BR', 'STRONG', 'EM', 'B', 'I', 'U', 'SPAN', 'UL', 'OL', 'LI']);

  const allowedClasses = new Set([
    'report-accent',
    'report-accent-strong',
    'report-lead',
    'report-list-intro',
    'dv-pilot-info',
  ]);

  const walk = (node) => {
    const children = Array.from(node.childNodes);

    children.forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child;
        const tagName = el.tagName.toUpperCase();

        if (!allowedTags.has(tagName)) {
          const fragment = document.createDocumentFragment();
          while (el.firstChild) {
            fragment.appendChild(el.firstChild);
          }
          el.replaceWith(fragment);
          walk(node);
          return;
        }

        Array.from(el.attributes).forEach((attr) => {
          const attrName = attr.name.toLowerCase();

          if (attrName === 'class') {
            const safeClasses = String(attr.value || '')
              .split(/\s+/)
              .map((cls) => cls.trim())
              .filter((cls) => allowedClasses.has(cls));

            if (safeClasses.length) {
              el.setAttribute('class', safeClasses.join(' '));
            } else {
              el.removeAttribute('class');
            }

            return;
          }

          el.removeAttribute(attr.name);
        });

        walk(el);
        return;
      }

      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove();
      }
    });
  };

  walk(wrapper);

  return wrapper.innerHTML.trim();
}

function HtmlInsightBody({ html, className = '' }) {
  const safeHtml = useMemo(() => sanitizeInsightHtml(html), [html]);

  if (!safeHtml) return null;

  return <div className={className} dangerouslySetInnerHTML={{ __html: safeHtml }} />;
}

function PlainInsightBody({ text, index }) {
  const paragraphs = splitParagraphs(text);
  if (!paragraphs.length) return null;

  return (
    <>
      {paragraphs.map((paragraph, paragraphIndex) => {
        const lines = splitLines(paragraph).filter((line) => line.trim() !== '');
        if (!lines.length) return null;

        if (lines.length === 1 && isTitleLikeLine(lines[0])) {
          const hasNextContent = paragraphs
            .slice(paragraphIndex + 1)
            .some((nextParagraph) => String(nextParagraph || '').trim());

          if (hasNextContent) {
            return (
              <div key={`ins-${index}-h-${paragraphIndex}`} className="dv-insight-subtitle">
                {lines[0].trim()}
              </div>
            );
          }

          return (
            <p key={`ins-${index}-p-${paragraphIndex}`} className="dv-insight-paragraph">
              {lines[0].trim()}
            </p>
          );
        }

        return (
          <p key={`ins-${index}-p-${paragraphIndex}`} className="dv-insight-paragraph">
            {lines.map((line, lineIndex) => {
              const formatted = formatLabelLine(line);

              return (
                <span key={`ins-${index}-p-${paragraphIndex}-l-${lineIndex}`}>
                  {formatted ? (
                    <>
                      <strong>{formatted.label}:</strong>
                      {formatted.rest ? ` ${formatted.rest}` : ''}
                    </>
                  ) : (
                    line
                  )}
                  {lineIndex < lines.length - 1 ? <br /> : null}
                </span>
              );
            })}
          </p>
        );
      })}
    </>
  );
}

function EntryBlock({ block, index, progressRatio = 0 }) {
  const title = typeof block?.title === 'string' ? block.title.trim() : '';
  const rawText =
    typeof block?.text === 'string'
      ? block.text
      : typeof block?.body === 'string'
        ? block.body
        : '[EMPTY]';

  const text = stripFutureSentenceAtCompletion(rawText, progressRatio);

  if (!text.trim() && !title) return null;

  const isHtml = containsHtml(text);
  const hasRenderableContent = isHtml
    ? Boolean(sanitizeInsightHtml(text))
    : splitParagraphs(text).length > 0 || Boolean(title);

  if (!hasRenderableContent) return null;

  const className = [
    'dv-insight-card',
    isHtml ? 'dv-insight-card--html' : 'dv-insight-card--plain',
    typeof block?.className === 'string' ? block.className.trim() : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div key={block?.id || `block-${index}`} className={className}>
      {title ? (
        <div className="dv-insight-title">
          <strong>{title}</strong>
        </div>
      ) : null}

      <div className="dv-insight-text">
        {isHtml ? (
          <HtmlInsightBody html={text} className="dv-insight-html" />
        ) : (
          <PlainInsightBody text={text} index={index} />
        )}
      </div>
    </div>
  );
}

function getEntryTitle(entry) {
  if (typeof entry?.title === 'string' && entry.title.trim()) {
    return entry.title.trim();
  }

  return '';
}

function RailEntry({ entry, progressRatio = 0 }) {
  const blocks = asArray(entry?.blocks);
  const entryTitle = getEntryTitle(entry);

  if (!blocks.length) return null;

  const className = [
    'dv-insights-entry',
    entry?.role ? `dv-insights-entry--${String(entry.role).trim()}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={className} data-entry-id={entry?.id || ''}>
      {entryTitle ? <h5 className="dv-insights-entry__title">{entryTitle}</h5> : null}

      {blocks.map((block, index) => (
        <EntryBlock
          key={block?.id || `${entry?.id || 'entry'}:block:${index}`}
          block={block}
          index={index}
          progressRatio={progressRatio}
        />
      ))}
    </section>
  );
}

function RailSection({ title, entries, progressRatio = 0, sectionClassName = '' }) {
  const safeEntries = asArray(entries);

  if (!safeEntries.length) return null;

  const className = ['dv-insights-section', sectionClassName].filter(Boolean).join(' ');

  return (
    <section className={className}>
      {title ? <h4 className="dv-insights-section__title">{title}</h4> : null}

      <div className="dv-insights-section__entries">
        {safeEntries.map((entry, index) => (
          <RailEntry
            key={entry?.id || `entry-${index}`}
            entry={entry}
            progressRatio={progressRatio}
          />
        ))}
      </div>
    </section>
  );
}

function getAssistantPersona(entries, explicitPersona) {
  const safeExplicitPersona = asObject(explicitPersona);

  if (Object.keys(safeExplicitPersona).length) {
    return safeExplicitPersona;
  }

  for (const entry of asArray(entries)) {
    const persona = asObject(entry?.persona);
    if (Object.keys(persona).length) {
      return persona;
    }

    const rawPersona = asObject(entry?.raw?.persona);
    if (Object.keys(rawPersona).length) {
      return rawPersona;
    }
  }

  return {};
}

function buildTypingPreset(persona, selectedTypingMode) {
  const safePersona = asObject(persona);
  const typing = asObject(safePersona?.typing);
  const baseSpeed = asObject(typing?.speed);

  const baseMin = Number(baseSpeed?.min);
  const baseMax = Number(baseSpeed?.max);

  const safeMin = Number.isFinite(baseMin) ? baseMin : 28;
  const safeMax = Number.isFinite(baseMax) ? baseMax : 42;

  if (selectedTypingMode === 'instant') {
    return {
      speed: {
        min: 0,
        max: 0,
      },
      initialDelay: 0,
      thinkChance: 0,
      thinkExtraMin: 0,
      thinkExtraMax: 0,
      typoChance: 0,
    };
  }

  if (selectedTypingMode === 'fast') {
    return {
      speed: {
        min: 10,
        max: 20,
      },
      initialDelay: 0,
      thinkChance: 0,
      thinkExtraMin: 0,
      thinkExtraMax: 0,
      typoChance: 0,
    };
  }

  return {
    speed: {
      min: safeMin,
      max: safeMax,
    },
    initialDelay: 120,
    thinkChance: Number.isFinite(Number(typing?.thinkChance)) ? Number(typing.thinkChance) : 0.03,
    thinkExtraMin: Number.isFinite(Number(typing?.thinkExtraMin))
      ? Number(typing?.thinkExtraMin)
      : 400,
    thinkExtraMax: Number.isFinite(Number(typing?.thinkExtraMax))
      ? Number(typing?.thinkExtraMax)
      : 900,
    typoChance: Number.isFinite(Number(typing?.typoChance)) ? Number(typing?.typoChance) : 0.008,
  };
}

function CoachSection({
  title,
  entries,
  assistantPersona = null,
  selectedCoachId = '',
  selectedTypingMode = 'normal',
  currentStepId = '',
  onCoachChoiceSelect = null,
  onCoachFeedback = null,
  onRestartCoachChoices = null,
  closedCoachFeedbackIds = [],
  hiddenCoachChoiceBlockIds = [],
  isCoachInteractionLoading = false,
}) {
  const safeEntries = asArray(entries);

  if (!safeEntries.length) return null;

  const persona = getAssistantPersona(safeEntries, assistantPersona);
  const personaName = asString(persona?.name).trim();
  const personaSlogan = asString(persona?.slogan).trim();
  const personaAvatarSrc = resolveCoachAvatarSrc(persona?.avatar?.src);
  const avatarFallback = personaName ? personaName.charAt(0).toUpperCase() : 'C';

  const typingPreset = buildTypingPreset(persona, selectedTypingMode);
  const isInstant = selectedTypingMode === 'instant';

  const safeCoachId = asString(selectedCoachId).trim() || 'system';
  const safeTypingMode = asString(selectedTypingMode).trim() || 'normal';
  const safeCurrentStepId = asString(currentStepId).trim() || 'unknown-step';

  const sessionKey = `module-rail::${safeCurrentStepId}::${safeCoachId}::${safeTypingMode}`;

  return (
    <section
      className="dv-insights-section dv-insights-section--assistant"
      data-coach-id={safeCoachId}
      data-typing-mode={safeTypingMode}
    >
      <div className="dv-insights-section__entries">
        <CoachStream
          entries={safeEntries}
          name={personaName || title || 'Coach'}
          slogan={personaSlogan}
          avatarSrc={personaAvatarSrc}
          avatarText={avatarFallback}
          speed={typingPreset}
          instant={isInstant}
          autoScroll={true}
          sessionKey={sessionKey}
          onChoiceSelect={onCoachChoiceSelect}
          onCoachFeedback={onCoachFeedback}
          onRestartChoices={onRestartCoachChoices}
          closedFeedbackIds={closedCoachFeedbackIds}
          hiddenChoiceBlockIds={hiddenCoachChoiceBlockIds}
          isInteractionLoading={isCoachInteractionLoading}
        />
      </div>
    </section>
  );
}

export default function InsightsPanel({
  insightEntries = [],
  contentEntries = [],
  assistantEntries = [],
  enabled = true,
  progressRatio = 0,
  selectedCoachId = '',
  selectedTypingMode = 'normal',
  assistantTitle = 'Coach',
  assistantPersona = null,
  currentStepId = '',
  onCoachChoiceSelect = null,
  onCoachFeedback = null,
  onRestartCoachChoices = null,
  closedCoachFeedbackIds = [],
  hiddenCoachChoiceBlockIds = [],
  isCoachInteractionLoading = false,
}) {
  const safeInsightEntries = asArray(insightEntries);
  const safeContentEntries = asArray(contentEntries);
  const safeAssistantEntries = asArray(assistantEntries);

  if (!enabled) {
    return <p className="dv-muted"></p>;
  }

  if (!safeInsightEntries.length && !safeContentEntries.length && !safeAssistantEntries.length) {
    return <p className="dv-muted"></p>;
  }

  return (
    <div className="dv-insights-list">
      <RailSection title="" entries={safeContentEntries} progressRatio={progressRatio} />
      <RailSection title="" entries={safeInsightEntries} progressRatio={progressRatio} />

      <CoachSection
        title={assistantTitle}
        entries={safeAssistantEntries}
        assistantPersona={assistantPersona}
        selectedCoachId={selectedCoachId}
        selectedTypingMode={selectedTypingMode}
        currentStepId={currentStepId}
        onCoachChoiceSelect={onCoachChoiceSelect}
        onCoachFeedback={onCoachFeedback}
        onRestartCoachChoices={onRestartCoachChoices}
        closedCoachFeedbackIds={closedCoachFeedbackIds}
        hiddenCoachChoiceBlockIds={hiddenCoachChoiceBlockIds}
        isCoachInteractionLoading={isCoachInteractionLoading}
      />
    </div>
  );
}
